import {
  accountResetCompletedSchema,
  accountResetRequestedSchema,
  accountResetRequestSchema,
  acknowledgeAlertRequestSchema,
  alertListSchema,
  alertQuerySchema,
  alertSchema,
  amendPersonResponseSchema,
  analyticsRangeQuerySchema,
  apiAnalyticsSchema,
  apiKeyPageSchema,
  apiKeySchema,
  apiKeyUsagePageSchema,
  auditActionCatalogueSchema,
  auditChainStatusSchema,
  auditEntryPageSchema,
  auditQuerySchema,
  backupAcceptedSchema,
  backupListSchema,
  bandLookupRequestSchema,
  bandLookupSchema,
  bulkCorrectionReportSchema,
  captureImageRequestSchema,
  changePasswordRequestSchema,
  clientConfigSchema,
  createApiKeyRequestSchema,
  createBackupRequestSchema,
  createdApiKeySchema,
  createdUserSchema,
  createExportRequestSchema,
  createFieldRequestSchema,
  createPersonRequestSchema,
  createPersonResponseSchema,
  createUserRequestSchema,
  credentialBatchListSchema,
  credentialBatchWithInventorySchema,
  credentialListQuerySchema,
  credentialListSchema,
  credentialSchema,
  csrfTokenSchema,
  cursorPageQuerySchema,
  diningAnalyticsSchema,
  diningRosterQuerySchema,
  diningRosterSchema,
  duplicateCandidateListSchema,
  duplicateCandidatesRequestSchema,
  emptyRequestSchema,
  exportCreatedSchema,
  exportListSchema,
  exportSchema,
  fieldDefinitionSchema,
  fieldListSchema,
  fieldPurgeReportSchema,
  generateCredentialBatchAcceptedSchema,
  generateCredentialBatchRequestSchema,
  importCredentialBatchQuerySchema,
  importCredentialBatchReportSchema,
  incidentPageSchema,
  incidentQuerySchema,
  integrityAnalyticsSchema,
  issueCredentialRequestSchema,
  issuedCredentialSchema,
  liveAnalyticsSchema,
  loggedOutSchema,
  loginRequestSchema,
  loginResponseSchema,
  maintenanceSetSchema,
  maintenanceStatusSchema,
  occupancySchema,
  operatorAnalyticsSchema,
  outsideListSchema,
  passwordUnlockRequestSchema,
  personDetailQuerySchema,
  personDetailSchema,
  personImageListSchema,
  personImageSchema,
  personImportAnalysisRequestSchema,
  personImportAnalysisSchema,
  personImportCommitRequestSchema,
  personImportCommitResponseSchema,
  personImportWorkbookQuerySchema,
  personImportWorkbookSchema,
  personListQuerySchema,
  personPageSchema,
  personRevisionListSchema,
  personThumbnailBatchSchema,
  personThumbnailsQuerySchema,
  pinUnlockRequestSchema,
  printedPassSchema,
  printPassRequestSchema,
  purgeArchivesReportSchema,
  purgeArchivesRequestSchema,
  reconciliationCandidatesSchema,
  reconciliationRequestSchema,
  registryAnalyticsSchema,
  resetPasswordResponseSchema,
  resetPasswordSubmitSchema,
  resetTotpResponseSchema,
  resetTotpSubmitSchema,
  resolveScanRequestSchema,
  resolveScanResponseSchema,
  restoreArmedSchema,
  restoreRequestSchema,
  revealedPassSchema,
  revealPassRequestSchema,
  revokeApiKeyRequestSchema,
  revokeCredentialRequestSchema,
  rollCallSchema,
  securityAnalyticsSchema,
  sessionSchema,
  setMaintenanceRequestSchema,
  setPinRequestSchema,
  settingsViewSchema,
  settingValueRequestSchema,
  settingViewSchema,
  shredPersonRequestSchema,
  shredPersonResponseSchema,
  submitMealRequestSchema,
  submitMealResponseSchema,
  submitPresenceRequestSchema,
  submitPresenceResponseSchema,
  systemAnalyticsSchema,
  totpConfirmationSchema,
  totpConfirmRequestSchema,
  totpEnrolmentOfferSchema,
  trafficAnalyticsSchema,
  trafficQuerySchema,
  unlockResponseSchema,
  updateFieldRequestSchema,
  updatePersonRequestSchema,
  updateUserRequestSchema,
  userListSchema,
  userSchema,
  voidCredentialBatchReportSchema,
  voidCredentialBatchRequestSchema,
  voidCredentialBlankReportSchema,
  voidCredentialBlankRequestSchema,
} from '@sentra/contracts';
import type * as z from 'zod';

/**
 * The routes this application calls, each naming the contract schema its answer is parsed with.
 *
 * **No response type is written by hand anywhere in this package.** Every one is `z.output` of a
 * schema that `@sentra/contracts` owns and the server validates its own answers against, so a
 * shape that changes on one side stops compiling on the other. That is the whole of what "a typed
 * client generated from the contracts package" buys, and it is worth more than a code generator
 * would be: there is no generated file to regenerate and no moment where the two are out of step.
 *
 * **The table grows; it is never re-derived.** Each front-end phase appends the routes its screens
 * call, exactly as `packages/server/src/auth/matrix.ts` grows on the other side. A `web`-tier test
 * checks every entry here against `openapi/openapi.json` — the document generated from the server's
 * own registries — so a method or a path that drifts is a failing test rather than a 404 somebody
 * meets at a gate.
 *
 * **`idempotencyInBody` marks the four routes whose request contract declares the key as a
 * member.** The header is sent on every unsafe request regardless, because the server's middleware
 * prefers it and because a retry that becomes a second write is the failure this exists to prevent;
 * on these four the same value also goes into the body, since the schema requires it and a caller
 * should not have to mint the same uuid twice.
 */

/** The methods this client issues. */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** The methods that change something, and therefore carry a CSRF token and an idempotency key. */
export const UNSAFE_METHODS: ReadonlySet<HttpMethod> = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * The parameter names a path template declares, read off the template itself.
 *
 * `/api/v1/persons/:id/images/:imageId` yields `'id' | 'imageId'`, so a call that forgets one, or
 * misspells one, does not compile. The alternative — a loose `Record<string, string>` — turns a
 * renamed parameter into a request that reaches the server with a literal `:imageId` in its path.
 */
export type PathParams<TPath extends string> = TPath extends `${string}:${infer Rest}`
  ? Rest extends `${infer Name}/${infer Tail}`
    ? Name | PathParams<`/${Tail}`>
    : Rest
  : never;

export interface RouteSpec<
  TPath extends string = string,
  TResponse extends z.ZodType = z.ZodType,
  TBody extends z.ZodType | null = z.ZodType | null,
  TMultipart extends boolean = boolean,
  TQuery extends z.ZodType | null = z.ZodType | null,
> {
  /** The server's own route id, so a failure names something both sides recognise. */
  readonly id: string;
  readonly method: HttpMethod;
  readonly path: TPath;
  /** The contract schema this route's 2xx body is parsed with. */
  readonly response: TResponse;
  /** The contract schema its request body is validated against, or `null` for a route with none. */
  readonly body: TBody;
  /** True on the four routes whose request contract carries `idempotencyKey` as a member. */
  readonly idempotencyInBody: boolean;
  /**
   * True on the one route whose request is a file part rather than a JSON document.
   *
   * It is a property of the ROUTE rather than an option at the call site, so the client can demand
   * a `FormData` where the server demands one and refuse a JSON body there — and so the photograph
   * upload still travels through the one place that attaches the CSRF token, the idempotency key
   * and the language, instead of a `fetch` of its own beside it.
   */
  readonly multipart: TMultipart;
  /**
   * The contract schema this route's query string is described by, or `null` for a route this
   * client calls without one.
   *
   * **It is the schema the SERVER validates with, imported by name** — the same discipline
   * `packages/server/src/openapi/registry.ts` is held to, and for the same reason: a second
   * opinion about what a route accepts is a second thing to keep correct, and the one that drifts
   * is always the copy. {@link QueryOf} types every call site from it, so a parameter the route
   * does not declare cannot be written at a `query:` position; a route that declares `null` cannot
   * be given a query at all.
   *
   * A route may legitimately declare `null` while the API documents optional parameters for it:
   * that says "this client sends none", which is a statement about the browser rather than about
   * the API. What it may NOT do is leave a documented REQUIRED parameter undeclared, and the
   * drift test in `api-client.web.test.tsx` refuses exactly that.
   */
  readonly query: TQuery;
}

/**
 * Any route, for a signature that does not care which.
 *
 * Every generic is widened to its constraint. Leaving one to its default is not a smaller version
 * of this line — it PINS that member, so a route declaring anything else stops satisfying
 * `TRoute extends AnyRoute` and `ALL_ROUTES`, `ApiClient.request` and `useApiMutation` all stop
 * compiling at once.
 */
export type AnyRoute = RouteSpec<string, z.ZodType, z.ZodType | null, boolean, z.ZodType | null>;

/** What a route answers with, once parsed. */
export type ResponseOf<TRoute extends AnyRoute> = z.output<TRoute['response']>;

/**
 * What a route's body must be, or `never` for a route that takes none.
 *
 * **`idempotencyKey` is omitted, because the caller does not own it.** On the four routes whose
 * request contract declares it as a member, the key is minted by {@link ApiClient} once per
 * submission and merged into the body there (AD-30) — so demanding it here would make every screen
 * draw a uuid the client is about to replace, which is the shape that produces a key per HTTP
 * attempt instead of a key per submission. A route that declares the member and does NOT set
 * `idempotencyInBody` would therefore send a body without it; the drift test refuses exactly that,
 * by reading the member list out of the generated API document.
 */
export type BodyOf<TRoute extends AnyRoute> = TRoute['body'] extends z.ZodType
  ? Omit<z.input<TRoute['body']>, 'idempotencyKey'>
  : never;

/**
 * What a route's query string may be made of, or `never` for a route that takes none.
 *
 * **The KEY SET is the whole point, and the value types are deliberately loose.** A query string
 * carries strings; the server owns what they may say, and every answer is parsed against the
 * route's own response schema anyway. What a client can get wrong in a way nothing notices is
 * sending a parameter the route does not accept — a `z.strictObject` refuses it with a 400 that
 * reaches an operator as "Check the highlighted fields", pointing at no field on the screen. So
 * this maps over the schema's members and replaces each value with what a query string can hold.
 *
 * The map is homomorphic, so an optional member stays optional and a required one stays NAMED —
 * though `undefined` is admissible everywhere, because that is what {@link ApiClient} treats as
 * "do not send this parameter", and it is what lets a screen pass a filter it has not filled in.
 *
 * It does not make a wrong query impossible by itself, and the comment should not pretend it
 * does: an excess-property check fires on a fresh object literal and not on a variable of a wider
 * type. It closes the `query:` position at every call site, which is where the parameters are
 * actually written.
 */
export type QueryOf<TRoute extends AnyRoute> = TRoute['query'] extends z.ZodType
  ? { readonly [K in keyof z.input<TRoute['query']>]: string | number | boolean | undefined }
  : never;

function route<
  const TPath extends string,
  TResponse extends z.ZodType,
  TBody extends z.ZodType | null = null,
  TMultipart extends boolean = false,
  TQuery extends z.ZodType | null = null,
>(spec: {
  id: string;
  method: HttpMethod;
  path: TPath;
  response: TResponse;
  body?: TBody;
  idempotencyInBody?: boolean;
  multipart?: TMultipart;
  query?: TQuery;
}): RouteSpec<TPath, TResponse, TBody, TMultipart, TQuery> {
  return {
    id: spec.id,
    method: spec.method,
    path: spec.path,
    response: spec.response,
    body: (spec.body ?? null) as TBody,
    idempotencyInBody: spec.idempotencyInBody ?? false,
    multipart: (spec.multipart ?? false) as TMultipart,
    query: (spec.query ?? null) as TQuery,
  };
}

/**
 * A route whose answer is BYTES, declared for its path and for nothing else.
 *
 * The two image-delivery routes answer a JPEG, so there is no contract schema to parse an answer
 * against and no sensible way to hand one to {@link ApiClient}. They are fetched by the browser
 * itself, as the `src` of an `<img>`: that is what streams the bytes, honours the response's own
 * `Cache-Control` and carries the session cookie without this application assembling a blob URL and
 * remembering to revoke it.
 *
 * They are declared HERE anyway, in a table of their own, because the alternative is a path
 * literal written inside a component — outside the drift test that checks every path in this file
 * against the generated API document, which is precisely where a renamed route becomes a broken
 * photograph nobody notices until somebody is standing at a gate. Having no `response` member is
 * what makes one structurally unusable as an {@link AnyRoute}: a caller cannot pass it to the
 * client by mistake, because it does not type-check as one.
 */
export interface BinaryRouteSpec<TPath extends string = string> {
  readonly id: string;
  readonly method: HttpMethod;
  readonly path: TPath;
}

function binaryRoute<const TPath extends string>(
  spec: BinaryRouteSpec<TPath>,
): BinaryRouteSpec<TPath> {
  return spec;
}

export const apiRoutes = {
  /** The client bootstrap. Fetched before the first render; declared here so the drift test sees it. */
  systemConfig: route({
    id: 'system.config',
    method: 'GET',
    path: '/api/v1/config',
    response: clientConfigSchema,
  }),

  /** Where a browser with no session gets its first double-submit token. */
  authCsrf: route({
    id: 'auth.csrf',
    method: 'GET',
    path: '/api/v1/auth/csrf',
    response: csrfTokenSchema,
  }),
  authLogin: route({
    id: 'auth.login',
    method: 'POST',
    path: '/api/v1/auth/login',
    body: loginRequestSchema,
    response: loginResponseSchema,
  }),
  authSession: route({
    id: 'auth.session',
    method: 'GET',
    path: '/api/v1/auth/session',
    response: sessionSchema,
  }),
  /**
   * Signing out. It declares an EMPTY body rather than none: Express leaves `request.body`
   * undefined when no parser matched the content type, so a POST with nothing in it is refused by
   * the server's own `strict({})`. Declaring it is what makes the client send `{}` and say it is
   * JSON — and what the drift test compares against the document's own `requestBody`.
   */
  authLogout: route({
    id: 'auth.logout',
    method: 'POST',
    path: '/api/v1/auth/logout',
    body: emptyRequestSchema,
    response: loggedOutSchema,
  }),
  /**
   * Handing the terminal over, which is ONE act: the incoming operator's credentials are verified,
   * the outgoing session id is destroyed by the same `regenerate`, and a session for the new person
   * comes back. That is why the body is a sign-in and the answer is a `LoginResponse` — a route
   * that only ended the old session would leave the two shifts attributable to one another, which
   * is the whole thing AD-54 exists to prevent.
   */
  authSwitchOperator: route({
    id: 'auth.switch_operator',
    method: 'POST',
    path: '/api/v1/auth/switch-operator',
    body: loginRequestSchema,
    response: loginResponseSchema,
  }),
  authChangePassword: route({
    id: 'auth.change_password',
    method: 'POST',
    path: '/api/v1/auth/password',
    body: changePasswordRequestSchema,
    response: sessionSchema,
  }),
  /**
   * Setting the gate PIN answers with the ACCOUNT, not a session: it changes no privilege and
   * rotates nothing, so the token the caller holds is still bound to the id it was issued against.
   * The `hasPin` in the answer is what the client needs.
   */
  authSetPin: route({
    id: 'auth.set_pin',
    method: 'PUT',
    path: '/api/v1/auth/pin',
    body: setPinRequestSchema,
    response: userSchema,
  }),
  authPinUnlock: route({
    id: 'auth.pin_unlock',
    method: 'POST',
    path: '/api/v1/auth/pin/unlock',
    body: pinUnlockRequestSchema,
    response: unlockResponseSchema,
  }),
  authPasswordUnlock: route({
    id: 'auth.password_unlock',
    method: 'POST',
    path: '/api/v1/auth/unlock',
    body: passwordUnlockRequestSchema,
    response: unlockResponseSchema,
  }),
  authTotpBegin: route({
    id: 'auth.totp_begin',
    method: 'POST',
    path: '/api/v1/auth/totp',
    body: emptyRequestSchema,
    response: totpEnrolmentOfferSchema,
  }),
  /**
   * Section 1.7's self-service reset, which the sign-in screen offers when the installation has a
   * mail server. All three are anonymous and all three need a token from `authCsrf` first.
   */
  authResetRequest: route({
    id: 'auth.reset_request',
    method: 'POST',
    path: '/api/v1/auth/reset',
    body: accountResetRequestSchema,
    response: accountResetRequestedSchema,
  }),
  authResetPassword: route({
    id: 'auth.reset_password',
    method: 'POST',
    path: '/api/v1/auth/reset/password',
    body: resetPasswordSubmitSchema,
    response: accountResetCompletedSchema,
  }),
  authResetTotp: route({
    id: 'auth.reset_totp',
    method: 'POST',
    path: '/api/v1/auth/reset/totp',
    body: resetTotpSubmitSchema,
    response: accountResetCompletedSchema,
  }),
  authTotpConfirm: route({
    id: 'auth.totp_confirm',
    method: 'POST',
    path: '/api/v1/auth/totp/confirm',
    body: totpConfirmRequestSchema,
    response: totpConfirmationSchema,
  }),

  /**
   * The register's search. It is here in this phase rather than with the table it feeds, because
   * its 503 is a behaviour this phase owns: the projection refuses to serve until it is built
   * (AD-23), and an empty list in its place is the duplicate-record cascade the design exists to
   * prevent.
   */
  personList: route({
    id: 'person.list',
    method: 'GET',
    path: '/api/v1/persons',
    response: personPageSchema,
    query: personListQuerySchema,
  }),

  /**
   * The live field registry, which is what the registration form is RENDERED FROM (AD-16).
   *
   * The form has no hard-coded field list: an administrator adds a field and it appears, with its
   * label, its help text, its requirement level and its per-type validation record, on every
   * person's form with no code change. That is the whole reason core and admin-defined fields are
   * the same thing on the server, and this is where the browser meets it.
   */
  fieldList: route({
    id: 'field.list',
    method: 'GET',
    path: '/api/v1/fields',
    response: fieldListSchema,
  }),

  personGet: route({
    id: 'person.get',
    method: 'GET',
    path: '/api/v1/persons/:id',
    response: personDetailSchema,
    query: personDetailQuerySchema,
  }),
  personCreate: route({
    id: 'person.create',
    method: 'POST',
    path: '/api/v1/persons',
    body: createPersonRequestSchema,
    response: createPersonResponseSchema,
    idempotencyInBody: true,
  }),
  personPatch: route({
    id: 'person.patch',
    method: 'PATCH',
    path: '/api/v1/persons/:id',
    body: updatePersonRequestSchema,
    response: amendPersonResponseSchema,
    idempotencyInBody: true,
  }),
  /**
   * The as-you-type duplicate guard (AD-18).
   *
   * A POST with the values in the body rather than a GET with them in a query string, for the
   * reason AD-28 gives for a pass code: a name, a passport number and a country in a URL are
   * personal data written in cleartext into two access logs, a browser history and any `Referer`,
   * all outside the encryption boundary and rotated rather than shredded.
   */
  personDuplicateCandidates: route({
    id: 'person.duplicate_candidates',
    method: 'POST',
    path: '/api/v1/persons/duplicate-candidates',
    body: duplicateCandidatesRequestSchema,
    response: duplicateCandidateListSchema,
  }),

  personImageList: route({
    id: 'person.image_list',
    method: 'GET',
    path: '/api/v1/persons/:id/images',
    response: personImageListSchema,
  }),
  /**
   * A photograph, as a file part.
   *
   * The one multipart route in this application. The server reads exactly one part, named `file`,
   * and decides what the bytes ARE by sniffing them rather than by believing the part's declared
   * media type (AD-52).
   */
  personImageUpload: route({
    id: 'person.image_upload',
    method: 'POST',
    path: '/api/v1/persons/:id/images',
    response: personImageSchema,
    multipart: true,
  }),
  /**
   * A webcam frame, base64, with no data-URL prefix.
   *
   * A different route from the upload rather than a flag on it, because the two differ in the one
   * place a route must not be ambiguous: the body. It is also what records `capturedVia: webcam`,
   * which is a fact an operator can see with their own eyes and which the print layout and the
   * audit trail both distinguish.
   */
  personImageCapture: route({
    id: 'person.image_capture',
    method: 'POST',
    path: '/api/v1/persons/:id/images/capture',
    body: captureImageRequestSchema,
    response: personImageSchema,
  }),
  personImageRemove: route({
    id: 'person.image_remove',
    method: 'DELETE',
    path: '/api/v1/persons/:id/images/:imageId',
    response: personImageSchema,
  }),
  /**
   * Up to twenty-five thumbnails in one request, which is how this browser draws EVERY face.
   *
   * The single-image thumbnail route is deliberately absent from this file: every screen that drew
   * a face per row used to send one request per face, and a full residence's roll-call spent the
   * whole building's per-address budget in seconds. `api/thumbnails.ts` collects what a render asks
   * for and sends it here; `shell.web.test.tsx` fails if a source names the single route again.
   */
  personThumbnails: route({
    id: 'person.thumbnails',
    method: 'GET',
    path: '/api/v1/person-thumbnails',
    query: personThumbnailsQuerySchema,
    response: personThumbnailBatchSchema,
  }),

  /**
   * Reading the facility's intake workbook: raw typed cells, the header row, a proposed mapping.
   *
   * Multipart with one part named `file`, like the batch import. `sheet` names a sheet by position
   * when the workbook has several; absent, the server takes the first whose header it recognises.
   * It writes nothing and keeps nothing, so every later step sends the rows back.
   */
  personImportWorkbook: route({
    id: 'person.import_workbook',
    method: 'POST',
    path: '/api/v1/person-imports/workbook',
    response: personImportWorkbookSchema,
    multipart: true,
    query: personImportWorkbookQuerySchema,
  }),
  /**
   * The whole interpretation of the sheet under the operator's decisions, answered again on every
   * change. A read wearing a POST, because the rows are personal data and belong in a body.
   */
  personImportAnalysis: route({
    id: 'person.import_analysis',
    method: 'POST',
    path: '/api/v1/person-imports/analysis',
    body: personImportAnalysisRequestSchema,
    response: personImportAnalysisSchema,
  }),
  /**
   * Registering one chunk of ready rows, each through the same create a registration runs.
   *
   * `idempotencyInBody`, because the contract declares the key as a member: a chunk resent after a
   * lost answer must be a replay of the rows it created rather than a second copy of those people.
   */
  personImportCommit: route({
    id: 'person.import',
    method: 'POST',
    path: '/api/v1/person-imports',
    body: personImportCommitRequestSchema,
    response: personImportCommitResponseSchema,
    idempotencyInBody: true,
  }),

  /**
   * The desk check for a scanned band, which the registration form runs before it submits (AD-60).
   *
   * Its SUCCESS shape is what the form needs and an error payload could never carry: a band already
   * claimed answers `state: 'claimed'` WITH the holder's `short_ref`, so the operator is told which
   * record is holding the band in their hand rather than only that it is taken.
   */
  /**
   * The gate's one question: what is this code, and whose is it (AD-28).
   *
   * **A POST with the code in the body, and there is no other shape.** A pass code in a path or a
   * query string is written in cleartext into two access logs, the browser's history and any
   * `Referer`, all outside the encryption boundary — and a code plus a scan is full impersonation.
   *
   * **Its answer mints the idempotency key the submission then carries.** That is not a
   * convenience: AD-30 says a `rejected` outcome does not consume its key, so the operator's
   * reasoned retry has to be written under the same key the refusal did not spend. A key minted
   * by this client would be a different key on that second attempt, because the body changed.
   */
  presenceResolve: route({
    id: 'presence.resolve',
    method: 'POST',
    path: '/api/v1/presence/resolve',
    body: resolveScanRequestSchema,
    response: resolveScanResponseSchema,
  }),
  /**
   * One movement, decided under one row lock (AD-20).
   *
   * A refused MOVEMENT and a refused REQUEST leave the server through different doors and this
   * client has to keep them apart: a refusal the state machine decided is a 200 carrying the event,
   * the resulting state and a `refusal` code, because a `presence_events` row WAS written; a
   * request that never reached a decision is the error body. A client that rendered the first as a
   * success would show a green confirmation to somebody who was refused entry.
   */
  presenceSubmit: route({
    id: 'presence.submit',
    method: 'POST',
    path: '/api/v1/presence',
    body: submitPresenceRequestSchema,
    response: submitPresenceResponseSchema,
    idempotencyInBody: true,
  }),

  /** Showing a stored code on screen, which is a disclosure and is audited as one. */
  credentialReveal: route({
    id: 'credential.reveal',
    method: 'POST',
    path: '/api/v1/credentials/:id/reveal',
    body: revealPassRequestSchema,
    response: revealedPassSchema,
  }),
  /**
   * The A4 sheet, rendered by the SERVER and printed by the browser.
   *
   * The layout is not built here, and that is deliberate: the sheet carries a QR symbol at a
   * measured geometry and exactly four values, and a second implementation of it in the browser
   * would be a second answer to "what is printed on a pass" — with the print-only one being the
   * copy nobody looks at. Printing is also a separate AUDITED action from revealing, because an
   * installation running pre-printed bands has to be able to show a count of zero prints.
   */
  credentialPrint: route({
    id: 'credential.print',
    method: 'POST',
    path: '/api/v1/credentials/:id/print',
    body: printPassRequestSchema,
    response: printedPassSchema,
  }),

  credentialLookup: route({
    id: 'credential.lookup',
    method: 'POST',
    path: '/api/v1/credentials/lookup',
    body: bandLookupRequestSchema,
    response: bandLookupSchema,
  }),

  /** Every pass one person has ever held, which is a handful and therefore a list. */
  credentialList: route({
    id: 'credential.list',
    method: 'GET',
    path: '/api/v1/credentials',
    response: credentialListSchema,
    query: credentialListQuerySchema,
  }),
  /**
   * Issuing a pass, including the REISSUE that Section 1.11 promises for a replaced one.
   *
   * `supersedeExisting` is what makes it a reissue rather than a refusal: a first issue is
   * protected by a partial unique index over the active rows, and a reissue retires the existing
   * row before it inserts — precisely so that index cannot fire. That is why the contract demands
   * an idempotency key here and why a double press of the button would otherwise supersede the
   * pass the first press minted, leaving the code the operator has already read aloud dead.
   */
  credentialIssue: route({
    id: 'credential.issue',
    method: 'POST',
    path: '/api/v1/credentials',
    body: issueCredentialRequestSchema,
    response: issuedCredentialSchema,
    idempotencyInBody: true,
  }),
  credentialRevoke: route({
    id: 'credential.revoke',
    method: 'POST',
    path: '/api/v1/credentials/:id/revoke',
    body: revokeCredentialRequestSchema,
    response: credentialSchema,
  }),

  /** One record's history (AD-25). Bounded by its edits, so a list rather than a page. */
  personRevisions: route({
    id: 'person.revisions',
    method: 'GET',
    path: '/api/v1/persons/:id/revisions',
    response: personRevisionListSchema,
  }),
  /**
   * AD-10's erasure, which is the one irreversible row action in the product.
   *
   * The request carries the record's own `short_ref` typed out, so the operation cannot be
   * completed by clicking through a dialog: the person doing it has to have the record in front of
   * them. `already_erased` is a SUCCESS rather than a refusal, because a second shred of one person
   * is specified as a no-op and a retried submission must not produce a second ledger row.
   */
  personShred: route({
    id: 'person.shred',
    method: 'POST',
    path: '/api/v1/persons/:id/shred',
    body: shredPersonRequestSchema,
    response: shredPersonResponseSchema,
  }),

  // -----------------------------------------------------------------------------------------------
  // Administration: the staff, the field registry, the settings and the machine credentials.
  // -----------------------------------------------------------------------------------------------

  /** The staff of one facility, which is tens of rows and therefore never paged. */
  userList: route({
    id: 'user.list',
    method: 'GET',
    path: '/api/v1/users',
    response: userListSchema,
  }),
  /**
   * Adding an account (AD-26).
   *
   * The answer carries `temporaryPassword`, and that value exists in that response and nowhere
   * else: nothing stores it and no later `GET` can produce it. The screen that shows it says so.
   */
  userCreate: route({
    id: 'user.create',
    method: 'POST',
    path: '/api/v1/users',
    body: createUserRequestSchema,
    response: createdUserSchema,
  }),
  /** Amending an account. Disabling is a `status`, because no account is ever deleted (AD-26). */
  userPatch: route({
    id: 'user.patch',
    method: 'PATCH',
    path: '/api/v1/users/:id',
    body: updateUserRequestSchema,
    response: userSchema,
  }),
  /** A reset mints, so the temporary password is never null here. Shown once, exactly as above. */
  userResetPassword: route({
    id: 'user.reset_password',
    method: 'POST',
    path: '/api/v1/users/:id/password',
    body: emptyRequestSchema,
    response: resetPasswordResponseSchema,
  }),
  /** Clearing an authenticator enrolment and its recovery codes, and nothing else. */
  userResetTotp: route({
    id: 'user.reset_totp',
    method: 'POST',
    path: '/api/v1/users/:id/totp/reset',
    body: emptyRequestSchema,
    response: resetTotpResponseSchema,
  }),

  /** Adding a field to the registry every person form is rendered from (AD-16). */
  fieldCreate: route({
    id: 'field.create',
    method: 'POST',
    path: '/api/v1/fields',
    body: createFieldRequestSchema,
    response: fieldDefinitionSchema,
  }),
  /**
   * Amending one.
   *
   * `key` and `type` are absent from the request contract and the object is strict, so a client
   * that tried to retype a field is refused rather than silently ignored: the field id is inside
   * the AAD of every value encrypted under it, so a retype would strand every existing value
   * (AD-17). The editor explains that in the interface rather than waiting for the refusal.
   */
  fieldAmend: route({
    id: 'field.amend',
    method: 'PATCH',
    path: '/api/v1/fields/:id',
    body: updateFieldRequestSchema,
    response: fieldDefinitionSchema,
  }),
  /**
   * Destroying a RETIRED field's stored values, for every person, irreversibly.
   *
   * It takes no body at all — the document declares none — which is why the confirmation is a typed
   * phrase in the interface rather than a member of a request. Super-admin only, exactly as an
   * erasure is, because it is the other bulk deletion in the product.
   */
  fieldPurge: route({
    id: 'field.purge',
    method: 'POST',
    path: '/api/v1/fields/:id/purge',
    response: fieldPurgeReportSchema,
  }),

  /** The whole settings registry in one response, with whatever is currently incoherent. */
  settingList: route({
    id: 'setting.list',
    method: 'GET',
    path: '/api/v1/settings',
    response: settingsViewSchema,
  }),
  /**
   * Writing one key.
   *
   * One key per request rather than a form-wide PATCH, because the coherence rules span keys and a
   * partial write of a multi-key rule is a state nobody can reason about. A secret key's value is
   * never returned — `settingViewSchema.value` is null for one whether or not something is stored —
   * so the panel renders "configured" and never the value.
   */
  settingUpdate: route({
    id: 'setting.update',
    method: 'PUT',
    path: '/api/v1/settings/:key',
    body: settingValueRequestSchema,
    response: settingViewSchema,
  }),

  /**
   * The machine credentials (AD-34), newest first, a page at a time.
   *
   * **It declares its query, and until it did the screen could not page.** A route with no `query`
   * resolves `QueryOf` to `never`, so the client could not send `limit` or `cursor` however the
   * server was written — and the server has paged this since it was written, at the default
   * twenty-five. The result was a screen that showed the first twenty-five keys and looked
   * complete. No `direction`: the handler answers newest-first unconditionally.
   */
  apiKeyList: route({
    id: 'api_key.list',
    method: 'GET',
    path: '/api/v1/api-keys',
    response: apiKeyPageSchema,
    query: cursorPageQuerySchema,
  }),
  /**
   * Issuing one. The ONLY response in the product that carries an API secret.
   *
   * `apiKeySchema` has no member for it, so no later read can produce one even by accident. The
   * screen shows it once, with an explicit "you will not see this again", and forgets it on
   * navigation.
   */
  apiKeyCreate: route({
    id: 'api_key.create',
    method: 'POST',
    path: '/api/v1/api-keys',
    body: createApiKeyRequestSchema,
    response: createdApiKeySchema,
  }),
  /** Withdrawing one. The body is strict and EMPTY: there is no version to guard on. */
  apiKeyRevoke: route({
    id: 'api_key.revoke',
    method: 'POST',
    path: '/api/v1/api-keys/:id/revoke',
    body: revokeApiKeyRequestSchema,
    response: apiKeySchema,
  }),
  /** One key's usage log, newest first and paged. `recordsReturned` is what AD-37's alert counts. */
  apiKeyUsage: route({
    id: 'api_key.usage',
    method: 'GET',
    path: '/api/v1/api-keys/:id/usage',
    response: apiKeyUsagePageSchema,
    query: cursorPageQuerySchema,
  }),

  // -----------------------------------------------------------------------------------------------
  // The trail, the artifacts, the archives and the alerts.
  // -----------------------------------------------------------------------------------------------

  /** The audit trail, filtered and paged. Reading it writes nothing. */
  auditList: route({
    id: 'audit.list',
    method: 'GET',
    path: '/api/v1/audit',
    response: auditEntryPageSchema,
    query: auditQuerySchema,
  }),
  /**
   * Is the trail intact?
   *
   * A LIVE verification on every call, plus the recorded high-water mark beside it. Both of the
   * verifier's own checks read only the rows that are still there, so a deleted TAIL leaves a chain
   * that verifies and a sequence contiguous from 1 to a smaller number; the remembered highest
   * sequence is the only thing that sees that, which is why the indicator renders both.
   */
  auditChain: route({
    id: 'audit.chain',
    method: 'GET',
    path: '/api/v1/audit/chain',
    response: auditChainStatusSchema,
  }),
  /**
   * The vocabulary the trail is written in, labelled in both languages.
   *
   * Served rather than bundled: `contracts/schemas/audit.ts` refuses to copy a hundred-odd literals
   * into this bundle because a second list is free to disagree with the first, and an administrator
   * cannot be asked to type `auth.login` either. Fetched once and effectively static, so the hook
   * that reads it holds it for the session.
   */
  auditActions: route({
    id: 'audit.actions',
    method: 'GET',
    path: '/api/v1/audit/actions',
    response: auditActionCatalogueSchema,
  }),

  /** The artifacts this installation still holds, filtered by kind. */
  exportList: route({
    id: 'export.list',
    method: 'GET',
    path: '/api/v1/exports',
    response: exportListSchema,
  }),
  /**
   * Asking for one. A 202 and a row to poll, because generating it is a job.
   *
   * The answer carries the one-time password the artifact is sealed under, which is shown once and
   * stored nowhere: an administrator who loses it re-requests the export rather than recovering the
   * file, which is the correct trade for a file holding decrypted personal data.
   */
  exportCreate: route({
    id: 'export.create',
    method: 'POST',
    path: '/api/v1/exports',
    body: createExportRequestSchema,
    response: exportCreatedSchema,
  }),
  /** One artifact's row, which is what a screen polls until `status` reaches `ready`. */
  exportGet: route({
    id: 'export.get',
    method: 'GET',
    path: '/api/v1/exports/:id',
    response: exportSchema,
  }),

  /** The archives this installation still holds (AD-38). */
  backupList: route({
    id: 'backup.list',
    method: 'GET',
    path: '/api/v1/backups',
    response: backupListSchema,
  }),
  /**
   * Taking one. A 202, because the api scrubs the master passphrase at boot and the WORKER archives.
   *
   * `includeObjects` is the difference between AD-38's two archives: the full one an administrator
   * downloads, and the database-only one the nightly schedule mails.
   */
  backupCreate: route({
    id: 'backup.create',
    method: 'POST',
    path: '/api/v1/backups',
    body: createBackupRequestSchema,
    response: backupAcceptedSchema,
  }),
  /**
   * ARMING a restore (AD-49), which is as far as the panel can go.
   *
   * A restore terminates every application backend so the database can be replaced, which stops the
   * very process serving this request; giving the application a Docker socket to orchestrate its
   * own execution would hand it root on the host. So this verifies the archive, shows its manifest,
   * takes the typed confirmation, writes maintenance mode plus the pending marker, and hands back
   * the exact `cli` command for somebody to run.
   */
  backupRestore: route({
    id: 'backup.restore',
    method: 'POST',
    path: '/api/v1/backups/restore',
    body: restoreRequestSchema,
    response: restoreArmedSchema,
  }),
  /**
   * Destroying every retained archive, now (Section 1.14 item 2).
   *
   * An erasure's latency equals the archive retention window: a shredded person's wrapped key still
   * exists inside every archive taken before the shred, and a dump is encrypted whole so there is
   * nothing to remove from one. This is the control that answers that, and it destroys the
   * cleartext pre-restore snapshots beside the archives as well. It removes the installation's own
   * recovery path, so the contract demands the word typed in the REQUEST and not only in a dialog.
   */
  backupPurge: route({
    id: 'backup.purge',
    method: 'POST',
    path: '/api/v1/backups/purge',
    body: purgeArchivesRequestSchema,
    response: purgeArchivesReportSchema,
  }),
  /**
   * Maintenance mode, which is what the restore's live status view polls.
   *
   * It answers at all only once the api is back, which is the whole mechanism: a panel polling this
   * sees a transport failure, then a gateway error, then this — and `pendingRestore` going false is
   * the api reporting that it has cold-started against the restored key material.
   */
  maintenanceStatus: route({
    id: 'system.maintenance_status',
    method: 'GET',
    path: '/api/v1/maintenance',
    response: maintenanceStatusSchema,
  }),
  maintenanceSet: route({
    id: 'system.maintenance',
    method: 'PUT',
    path: '/api/v1/maintenance',
    body: setMaintenanceRequestSchema,
    response: maintenanceSetSchema,
  }),

  /**
   * Who the restored archive believes is OUTSIDE, which is the outside monitor's own query.
   *
   * The small set somebody can walk over and check, rather than the register-sized list on the
   * other side; somebody recorded inside who has left is caught at a door instead, as an anomaly.
   */
  reconciliationCandidates: route({
    id: 'presence.reconcile_candidates',
    method: 'GET',
    path: '/api/v1/presence/reconciliation',
    response: reconciliationCandidatesSchema,
  }),
  /** Correcting them. One run, one audit row, and a report of what it actually moved. */
  reconcile: route({
    id: 'presence.reconcile',
    method: 'POST',
    path: '/api/v1/presence/reconciliation',
    body: reconciliationRequestSchema,
    response: bulkCorrectionReportSchema,
  }),
  /**
   * Serving one meal.
   *
   * `idempotencyInBody: true`, like the movement beside it, because the key is the one the SCAN
   * minted and the domain implements it rather than the HTTP middleware: a replay comes back as the
   * original serving with `replayed: true` rather than as a second confirmation.
   */
  diningSubmit: route({
    id: 'dining.submit',
    method: 'POST',
    path: '/api/v1/dining',
    body: submitMealRequestSchema,
    response: submitMealResponseSchema,
    idempotencyInBody: true,
  }),
  /** Who has eaten and who has not, on one civil day. The counter's roll-call. */
  diningRoster: route({
    id: 'dining.roster',
    method: 'GET',
    path: '/api/v1/dining/roster',
    response: diningRosterSchema,
    query: diningRosterQuerySchema,
  }),

  /** The printable evacuation list. The one panel in the product that carries a name (AD-47). */
  rollCall: route({
    id: 'presence.rollcall',
    method: 'GET',
    path: '/api/v1/presence/roll-call',
    response: rollCallSchema,
  }),

  /** Who is outside, and who among them is late back. The mirror of the roll-call. */
  outsideNow: route({
    id: 'presence.outside',
    method: 'GET',
    path: '/api/v1/presence/outside',
    response: outsideListSchema,
  }),

  /**
   * Both counts, the overdue figure and the allowance it was measured against.
   *
   * Counts only, which is what lets the landing screen a terminal sits on unattended poll it: there
   * is no name on it to leave on a screen nobody is standing at. The route declares `idle: 'none'`
   * for that polling, so refreshing it is not evidence that anybody is there.
   */
  presenceCounts: route({
    id: 'presence.occupancy',
    method: 'GET',
    path: '/api/v1/presence/occupancy',
    response: occupancySchema,
  }),

  /**
   * The incident log: what went wrong at a door, and what the operator wrote about it.
   *
   * The one route in this table that answers with decrypted prose about a person, which is why it
   * needs `presence:investigate` and why no API key can reach it.
   */
  incidentList: route({
    id: 'presence.incidents',
    method: 'GET',
    path: '/api/v1/presence/incidents',
    response: incidentPageSchema,
    query: incidentQuerySchema,
  }),

  /** The open alert set (AD-47). An alert is a row; an anomaly is a derived query. */
  alertList: route({
    id: 'alert.list',
    method: 'GET',
    path: '/api/v1/alerts',
    response: alertListSchema,
    // Declared although the browser sent none until the incidents screen wanted the acknowledged
    // set too: `QueryOf` types a call site from this member, so a route that declares nothing
    // resolves to `never` and the parameter cannot be sent at all. The server has documented it all
    // along (`openapi/registry.ts`), so this is the browser catching up rather than a contract move.
    query: alertQuerySchema,
  }),
  /** Acknowledging one. There is nothing to say, so the body is strict and empty. */
  alertAcknowledge: route({
    id: 'alert.acknowledge',
    method: 'POST',
    path: '/api/v1/alerts/:id/acknowledge',
    body: acknowledgeAlertRequestSchema,
    response: alertSchema,
  }),

  // -----------------------------------------------------------------------------------------------
  // Section 1.12's nine panels. One request per PANEL, never per metric.
  // -----------------------------------------------------------------------------------------------

  analyticsLive: route({
    id: 'analytics.live',
    method: 'GET',
    path: '/api/v1/analytics/live',
    response: liveAnalyticsSchema,
  }),
  analyticsTraffic: route({
    id: 'analytics.traffic',
    method: 'GET',
    path: '/api/v1/analytics/traffic',
    response: trafficAnalyticsSchema,
    query: trafficQuerySchema,
  }),
  analyticsRegistry: route({
    id: 'analytics.registry',
    method: 'GET',
    path: '/api/v1/analytics/registry',
    response: registryAnalyticsSchema,
    query: analyticsRangeQuerySchema,
  }),
  analyticsIntegrity: route({
    id: 'analytics.integrity',
    method: 'GET',
    path: '/api/v1/analytics/integrity',
    response: integrityAnalyticsSchema,
    query: analyticsRangeQuerySchema,
  }),
  analyticsDining: route({
    id: 'analytics.dining',
    method: 'GET',
    path: '/api/v1/analytics/dining',
    response: diningAnalyticsSchema,
    query: analyticsRangeQuerySchema,
  }),
  analyticsOperators: route({
    id: 'analytics.operators',
    method: 'GET',
    path: '/api/v1/analytics/operators',
    response: operatorAnalyticsSchema,
    query: analyticsRangeQuerySchema,
  }),
  analyticsSecurity: route({
    id: 'analytics.security',
    method: 'GET',
    path: '/api/v1/analytics/security',
    response: securityAnalyticsSchema,
  }),
  analyticsApi: route({
    id: 'analytics.api',
    method: 'GET',
    path: '/api/v1/analytics/api',
    response: apiAnalyticsSchema,
    query: analyticsRangeQuerySchema,
  }),
  analyticsSystem: route({
    id: 'analytics.system',
    method: 'GET',
    path: '/api/v1/analytics/system',
    response: systemAnalyticsSchema,
  }),

  // -----------------------------------------------------------------------------------------------
  // Pre-printed stock (AD-60 to AD-66). Reachable only in `preprinted` mode.
  // -----------------------------------------------------------------------------------------------

  credentialBatchList: route({
    id: 'credential_batch.list',
    method: 'GET',
    path: '/api/v1/credential-batches',
    response: credentialBatchListSchema,
  }),
  credentialBatchGet: route({
    id: 'credential_batch.get',
    method: 'GET',
    path: '/api/v1/credential-batches/:id',
    response: credentialBatchWithInventorySchema,
  }),
  /**
   * Minting a box inside the application (AD-64). A 202, because generation is a job.
   *
   * The answer carries the one-time password the artifact is sealed under, and it is the only copy:
   * `credential_blanks` stores a one-way keyed hash and never a code, so losing the password loses
   * the box's only plaintext. The screen says exactly that.
   */
  credentialBatchGenerate: route({
    id: 'credential_batch.generate',
    method: 'POST',
    path: '/api/v1/credential-batches',
    body: generateCredentialBatchRequestSchema,
    response: generateCredentialBatchAcceptedSchema,
  }),
  /**
   * Importing a supplier's file, as a multipart body with the file part named `file`.
   *
   * `?dryRun=true` runs every check and inserts nothing, which is the form an administrator uses
   * before committing two thousand bands. A dry run REPORTS its collisions; a real one refuses
   * whole, because a half-imported batch means bands in a box the desk refuses with no way to tell
   * which.
   */
  credentialBatchImport: route({
    id: 'credential_batch.import',
    method: 'POST',
    path: '/api/v1/credential-batches/import',
    response: importCredentialBatchReportSchema,
    multipart: true,
    query: importCredentialBatchQuerySchema,
  }),
  /** Withdrawing a whole box, which leaves already-claimed bands alone (AD-62). */
  credentialBatchVoid: route({
    id: 'credential_batch.void',
    method: 'POST',
    path: '/api/v1/credential-batches/:id/void',
    body: voidCredentialBatchRequestSchema,
    response: voidCredentialBatchReportSchema,
  }),
  /** Withdrawing ONE band, by serial. It records no reason, and the schema has no member for one. */
  credentialBlankVoid: route({
    id: 'credential_batch.blank_void',
    method: 'POST',
    path: '/api/v1/credential-batches/:id/blanks/:serial/void',
    body: voidCredentialBlankRequestSchema,
    response: voidCredentialBlankReportSchema,
  }),
} as const;

/**
 * The routes that never finish, declared for their path and for nothing else.
 *
 * One member, and the table exists for the same reason {@link binaryRoutes} does: the path belongs
 * in this file, inside the drift test that checks every path against the generated API document,
 * rather than as a literal inside a hook. What makes it structurally unusable as an
 * {@link AnyRoute} is having no `response` — an event stream has no body a schema could parse, and
 * a caller that passed this to `request` would wait for a response that is designed never to end.
 */
export const streamRoutes = {
  realtimeStream: binaryRoute({
    id: 'realtime.stream',
    method: 'GET',
    path: '/api/v1/realtime/stream',
  }),
} as const;

/**
 * The routes that answer bytes. See {@link BinaryRouteSpec} for why they are a separate table.
 *
 * The full-size photograph is here and its thumbnail is not: a thumbnail is drawn from the batched
 * route above, so that a screen of faces is one request rather than one per face.
 */
export const binaryRoutes = {
  personImageFull: binaryRoute({
    id: 'person.image_full',
    method: 'GET',
    path: '/api/v1/persons/:id/images/:imageId/full',
  }),
  /**
   * The three files an administrator downloads, none of which is JSON — and they are NOT fetched
   * the same way, which is the thing to know before adding a fourth.
   *
   * **`backupDownload` is a plain navigation**, for the reason the two photograph routes are: that
   * is what streams the bytes, carries the session cookie and honours the `Content-Disposition` the
   * server sets, where reading a multi-gigabyte archive through this client would mean buffering it
   * into a blob somebody has to remember to revoke.
   *
   * **The other two cannot be.** An export artifact and a batch artifact are sealed under a
   * one-time password that travels in `X-Export-Password`, because a query parameter would put the
   * secret that opens a list of live pass codes into two access logs and a browser history — and a
   * navigation cannot set a header. Both therefore go through `ApiClient.download`, which answers a
   * `Blob`, and `api/save-file.ts` hands it to the browser.
   *
   * All three are declared here so their paths stay inside the drift test.
   */
  exportDownload: binaryRoute({
    id: 'export.download',
    method: 'GET',
    path: '/api/v1/exports/:id/download',
  }),
  backupDownload: binaryRoute({
    id: 'backup.download',
    method: 'GET',
    path: '/api/v1/backups/:id/download',
  }),
  credentialBatchDownload: binaryRoute({
    id: 'credential_batch.download',
    method: 'GET',
    path: '/api/v1/credential-batches/:id/artifact',
  }),
} as const;

/** Every declared route, for the test that checks the table against the generated OpenAPI document. */
export const ALL_ROUTES: readonly AnyRoute[] = Object.values(apiRoutes);

/** The byte-answering routes, which the same test checks for path and method and nothing else. */
export const ALL_BINARY_ROUTES: readonly BinaryRouteSpec[] = Object.values(binaryRoutes);
